"use client";

import Image from "next/image";
import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import {
  ExternalLink,
  Phone,
  Users,
  FileSignature,
  Scale,
  UserCheck,
  User,
  Check,
  ArrowRight,
  Home,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";

const NAVY = "#102542";
const YELLOW = "#ffb800";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Movimiento%20de%20pasajeros/duty4.jpg";

type Caso = {
  id: string;
  icon: string;
  title: string;
  description: string;
  requisitos: string[];
};
type DocItem = { id: string; title: string; description: string };
type RecItem = { title: string; description: string };

const CASE_ICONS: Record<string, LucideIcon> = {
  users: Users,
  notary: FileSignature,
  judge: Scale,
  thirdParty: UserCheck,
  alone: User,
};

export default function ViajandoConMenoresPage() {
  const t = useTranslations("viajandoConMenoresPage");
  const tCrumb = useTranslations("breadcrumb");
  const tNav = useTranslations("nav.informacion.links");
  const locale = useLocale();

  const casos = t.raw("casos.items") as Caso[];
  const documentos = t.raw("documentos.items") as DocItem[];
  const recomendaciones = t.raw("recomendaciones.items") as RecItem[];

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("hero.title")}
          fill
          priority
          sizes="100vw"
          className="object-cover blur-xs"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs text-white/80 flex-wrap">
              <li>
                <Link
                  href={`/${locale}`}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span>{tCrumb("home")}</span>
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li>
                <Link
                  href={`/${locale}/informacion`}
                  className="hover:text-white transition-colors"
                >
                  {tNav("informacion")}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li
                aria-current="page"
                className="text-white font-medium truncate"
              >
                {t("hero.title")}
              </li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px" style={{ backgroundColor: YELLOW }} />
              {t("hero.eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("hero.title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Casos en que se permite la salida */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-4">
              <span
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold"
                style={{ color: NAVY }}
              >
                <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
                {t("casos.eyebrow")}
              </span>
              <span
                className="inline-flex items-center text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${YELLOW}1a`,
                  color: "#7a5a00",
                  border: `1px solid ${YELLOW}66`,
                }}
              >
                {t("article")}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("casos.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("casos.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {casos.map((caso) => {
              const Icon = CASE_ICONS[caso.icon] ?? User;
              return (
                <article
                  key={caso.id}
                  className="group relative bg-white p-8 flex flex-col transition-colors hover:bg-gray-50"
                >
                  {/* Accent strip top — appears on hover */}
                  <span
                    className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                    style={{ backgroundColor: YELLOW }}
                  />

                  {/* Header: number + icon */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-baseline gap-2">
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: YELLOW }}
                      />
                      <span
                        className="text-4xl font-bold tabular-nums tracking-tight leading-none"
                        style={{ color: NAVY }}
                      >
                        {caso.id}
                      </span>
                    </div>
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      style={{
                        backgroundColor: "#f5f5f5",
                        color: NAVY,
                      }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>

                  {/* Title + description */}
                  <h3 className="text-lg font-bold text-gray-900 tracking-tight mb-3">
                    {caso.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    {caso.description}
                  </p>

                  {/* Requirements */}
                  <div className="mt-auto pt-5 border-t border-gray-100">
                    <p className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-3">
                      <span
                        className="w-3 h-px"
                        style={{ backgroundColor: YELLOW }}
                      />
                      {t("casos.requisitosLabel")}
                    </p>
                    <ul className="space-y-2">
                      {caso.requisitos.map((req, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed"
                        >
                          <Check
                            className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                            style={{ color: NAVY }}
                          />
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Documentos extranjeros */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-4">
              <span
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold"
                style={{ color: NAVY }}
              >
                <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
                {t("documentos.eyebrow")}
              </span>
              <span
                className="inline-flex items-center text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${YELLOW}1a`,
                  color: "#7a5a00",
                  border: `1px solid ${YELLOW}66`,
                }}
              >
                {t("article")}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("documentos.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("documentos.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {documentos.map((item) => (
              <article
                key={item.id}
                className="bg-white p-8 flex flex-col gap-4 min-h-[200px] transition-colors hover:bg-white"
              >
                <span
                  className="text-4xl font-bold tabular-nums tracking-tight leading-none"
                  style={{ color: NAVY }}
                >
                  {item.id}
                </span>
                <h3 className="text-lg font-bold text-gray-900 tracking-tight mt-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </article>
            ))}
          </div>

          <div
            className="mt-8 max-w-3xl flex items-start gap-3 p-5 bg-white rounded-xl border border-gray-200"
            style={{ borderLeft: `3px solid ${YELLOW}` }}
          >
            <p className="text-sm text-gray-700 leading-relaxed">
              <span
                className="font-semibold uppercase tracking-wider text-[10px] mr-2 font-mono"
                style={{ color: "#7a5a00" }}
              >
                Nota
              </span>
              {t("documentos.exclusion")}
            </p>
          </div>
        </div>
      </section>

      {/* Recomendaciones */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("recomendaciones.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("recomendaciones.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("recomendaciones.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {recomendaciones.map((item, idx) => (
              <article
                key={idx}
                className="bg-white p-8 flex flex-col gap-3 min-h-[200px] transition-colors hover:bg-gray-50"
              >
                <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400 tabular-nums">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="text-base font-bold text-gray-900 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA contacto — fondo NAVY */}
      <section className="bg-[#102542]">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
                <span
                  className="w-8 h-px"
                  style={{ backgroundColor: YELLOW }}
                />
                {t("cta.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                {t("cta.title")}
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
                {t("cta.subtitle")}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={t("cta.primaryHref")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors hover:opacity-90"
                  style={{ backgroundColor: YELLOW, color: NAVY }}
                >
                  {t("cta.primary")}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a
                  href={`tel:${t("cta.phone").replace(/[^\d+]/g, "")}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-wider font-semibold border border-white/30 text-white transition-colors hover:border-white"
                >
                  <Phone className="w-3.5 h-3.5" />
                  {t("cta.phone")}
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
              <a
                href={`tel:${t("cta.phone").replace(/[^\d+]/g, "")}`}
                className="bg-[#102542] p-8 flex flex-col transition-colors hover:bg-[#0a1a30] group"
              >
                <p className="text-xs uppercase tracking-wider text-white/60 mb-3 font-mono">
                  {t("cta.phoneLabel")}
                </p>
                <p className="text-3xl md:text-4xl font-bold tabular-nums tracking-tight text-white inline-flex items-center gap-3 group-hover:gap-4 transition-all">
                  {t("cta.phone")}
                  <ArrowRight className="w-5 h-5" style={{ color: YELLOW }} />
                </p>
              </a>
              <div className="bg-[#102542] p-6 border-t border-white/10">
                <p className="text-xs text-white/50 leading-relaxed">
                  {t("source")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
